import Link from 'next/link'
import { NextSeo } from 'next-seo'
import { Header } from '@components/Header'
import { Footer } from '@components/Footer'
import { Button } from '@components/UI/Button'
import { ArrowRightIcon, ClockIcon } from '@heroicons/react/solid'
import { HandshakeIcon } from '@components/UI/Icons/handshake'
import { TrophyIcon } from '@components/UI/Icons/trophy'
import { MoneyBillIcon } from '@components/UI/Icons/moneyBill'
import { CommentsIcon } from '@components/UI/Icons/comments'
import { VideoIcon } from '@components/UI/Icons/video'
import { AffiliateContactForm } from '@components/AffiliateContactForm'

export default function RealEstateAgents() {
  return (
    <>
      <NextSeo
        title="Home Inspection Solutions For Realtors - Home Repair Pricing"
        description="Find zip-code specific repair estimates in a simple & clear report that outlines project details & cost estimates. Close more deals sooner."
        canonical="https://majordomo.com/repair-pricing/-realtors"
        openGraph={{
          url: 'https://majordomo.com/repair-pricing/-realtors',
          title:
            'Home Inspection Solutions For Realtors - Home Repair Pricing | Majordomo',
          description:
            'Find zip-code specific repair estimates in a simple & clear report that outlines project details & cost estimates. Close more deals sooner.',
          images: [
            {
              url:
                'https://majordomo.com/images/product-pages/re-agent-hero.png',
              width: 700,
              height: 681,
              alt:
                'A real estate agent sitting in a cafe working on the computer and phone'
            },
            {
              url:
                'https://majordomo.com/images/product-pages/request-list.png',
              width: 1816,
              height: 1992,
              alt: 'Screen shot of Majordomo’s request list'
            }
          ]
        }}
      />
      <Header />

      <main className="pt-8 px-0 container mx-auto">
        <div className="flex md:flex-row flex-col justify-between items-center lg:pb-36 pb-12">
          <div className="flex flex-col md:items-start items-center justify-center w-full 2xl:p-36 xl:p-20 md:p-8 md:order-first order-last">
            <div className="flex items-center border-b border-gray-200 mb-4 pb-4 md:w-full">
              <HandshakeIcon className="pr-2 mr-2 border-r border-gray-200 w-12 h-12 fill-current text-brand" />
              <h1 className="text-2xl font-black tracking-wide text-brand uppercase">
                Home Repair Costs For Realtors
              </h1>
            </div>

            <p className="lg:mb-8 md:mb-4 mb-8 lg:text-2xl md:text-lg text-xl font-light">
              <span className="font-black">Fewer</span> inspection worries,
              <br />
              <span className="font-black">Smarter</span> negotiations,
              <br />& <span className="font-black">More</span> closes
            </p>

            <p className="w-full max-w-lg md:p-0 px-8">
              Integrate Majordomo into your systems so you can step back and{' '}
              <span className="font-black">let Majordomo work for you</span>.
              You'll be able to focus on more high-touch activities that are the
              best use of your time, energy, skills, and expertise, all while
              you generate more profits.
            </p>
          </div>

          <img
            src="/images/product-pages/re-agent-hero.png"
            className="md:order-last order-first md:mb-0 mb-8"
            width="50%"
          />
        </div>

        <div className="lg:mb-36 mb-12 py-12">
          <h4 className="text-center md:text-2xl text-lg font-black tracking-tight md:px-0 px-8 lg:pb-0 pb-8 mx-auto">
            Transform your systems after the home inspection
          </h4>

          <div className="flex lg:flex-row flex-col justify-evenly lg:px-16 px-4">
            <div className="flex items-center justify-center lg:w-1/2 w-full lg:p-16">
              <img
                src="/images/product-pages/request-list.png"
                alt="Majordomo product samples"
                className="xl:max-w-xl lg:max-w-md max-w-xs"
              />
            </div>

            <div className="space-y-12 md:p-16 p-4 lg:w-1/2 w-full flex flex-col items-center">
              <div className="flex relative">
                <ClockIcon
                  width={56}
                  height={56}
                  className="absolute top-0 md:-left-24 md:w-16 w-12 md:h-16 h-12 text-white bg-gold-400 p-2"
                />

                <div>
                  <h4 className="max-w-lg mr-auto md:text-2xl text-lg font-black tracking-tight mb-2 md:mt-0 mt-16">
                    Save time after the inspection
                  </h4>
                  <p className="max-w-lg mr-auto">
                    The Domoreport's{' '}
                    <span className="font-black">
                      guaranteed 24-hour turnaround
                    </span>{' '}
                    lets you focus on the greatest concerns of your buyers and
                    start negotiations sooner.{' '}
                    <span className="font-black">No more running around</span>{' '}
                    or waiting for contractors to return calls.
                  </p>
                </div>
              </div>

              <div className="flex relative">
                <HandshakeIcon className="absolute top-0 md:-left-24 md:w-16 w-12 md:h-16 h-12 fill-current text-white bg-gold-400 p-2" />

                <div>
                  <h4 className="max-w-lg mr-auto md:text-2xl text-lg font-black tracking-tight mb-2 md:mt-0 mt-16">
                    Negotiate smarter with sellers
                  </h4>
                  <p className="max-w-lg mr-auto">
                    The Domoreport's localized repair estimates are organized
                    into a simple and clear report that outlines project details
                    and cost estimates, so negotiations can be based on facts,
                    not guesses. Use our "Request List" tool to build a
                    customized request list to send to the sellers;{' '}
                    <span className="font-black">
                      no more cut-and-paste addendum projects
                    </span>
                    . Save your custom request list addendum PDF for a
                    professional edge when negotiating for seller concessions.{' '}
                    <a
                      href="https://intercom.help/majordomo/en/articles/4641002-how-do-i-create-and-use-the-request-list"
                      className="text-accentBlue hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Learn more
                    </a>
                  </p>
                </div>
              </div>

              <div className="flex relative">
                <TrophyIcon className="absolute top-0 md:-left-24 md:w-16 w-12 md:h-16 h-12 fill-current text-white bg-gold-400 p-2" />

                <div>
                  <h4 className="max-w-lg mr-auto md:text-2xl text-lg font-black tracking-tight mb-2 md:mt-0 mt-16">
                    Close more transactions
                  </h4>
                  <p className="max-w-lg mr-auto">
                    Inspections can uncover problems that can scare off buyers.
                    A Domoreport will help buyers focus on solutions and inspire
                    them to move forward.
                  </p>
                </div>
              </div>

              <div className="flex relative self-start 2xl:ml-8 lg:ml-0 md:ml-12 sm:ml-8 ml-0">
                <h4 className="max-w-lg md:text-2xl text-lg font-black tracking-tight mb-2">
                  All for just $99
                  <span className="font-light text-base"> / per report</span>
                </h4>
              </div>
            </div>
          </div>

          <div className="flex md:flex-row flex-col justify-evenly items-center w-full md:space-y-0 space-y-4 md:pb-12 pb-0 md:pt-0 pt-8">
            <Link href="/checkout">
              <Button variant="primary" clipSide="right">
                Get repair estimates{' '}
                <ArrowRightIcon width={18} height={18} className="ml-2" />
              </Button>
            </Link>

            <Link href="/features/domoreport-repair-estimates">
              <a className="flex items-center justify-center font-bold text-accentBlue hover:underline">
                Learn More{' '}
                <ArrowRightIcon width={18} height={18} className="ml-2" />
              </a>
            </Link>
          </div>
        </div>

        <div className="bg-gray-50 lg:mb-36 mb-12 py-12">
          <h4 className="text-center md:text-2xl text-lg font-black tracking-tight md:px-0 px-8 lg:pb-0 pb-8 mx-auto">
            A partnership to help you scale
          </h4>

          <div className="flex lg:flex-row flex-col justify-evenly lg:px-16 px-4">
            <div className="space-y-12 lg:p-16 p-4 lg:w-1/2 w-full flex flex-col items-center">
              <div className="flex-relative">
                <p className="md:text-2xl text-lg font-heading">
                  Whether you're an established agent or just starting out,
                  Majordomo's partner program gives you the tools and resources
                  you need.
                </p>
              </div>

              <div className="flex relative">
                <MoneyBillIcon className="absolute top-0 md:-left-24 md:w-16 w-12 md:h-16 h-12 fill-current text-white bg-gold-400 p-2" />

                <div>
                  <h4 className="max-w-lg mr-auto md:text-2xl text-lg font-black tracking-tight mb-2 md:mt-0 mt-16">
                    Wholesale pricing
                  </h4>
                  <p className="max-w-lg mr-auto">
                    Offer the Domoreport as an included service enhancement. Or,
                    if you choose, offer your clients a code to purchase a
                    Domoreport at a discounted price.
                  </p>
                </div>
              </div>

              <div className="flex relative">
                <VideoIcon className="absolute top-0 md:-left-24 md:w-16 w-12 md:h-16 h-12 fill-current text-white bg-gold-400 p-2" />

                <div>
                  <h4 className="max-w-lg mr-auto md:text-2xl text-lg font-black tracking-tight mb-2 md:mt-0 mt-16">
                    Learn the way you want
                  </h4>
                  <p className="max-w-lg mr-auto">
                    Get the most out of the partner program with in-depth, live
                    and recorded training.
                  </p>
                </div>
              </div>

              <div className="flex relative">
                <CommentsIcon className="absolute top-0 md:-left-24 md:w-16 w-12 md:h-16 h-12 fill-current text-white bg-gold-400 p-2" />

                <div>
                  <h4 className="max-w-lg mr-auto md:text-2xl text-lg font-black tracking-tight mb-2 md:mt-0 mt-16">
                    Priority support
                  </h4>
                  <p className="max-w-lg mr-auto">
                    Majordomo is always ready to help with 24-hour chat and
                    customer support.
                  </p>
                </div>
              </div>

              <div className="flex relative">
                <ClockIcon
                  width={56}
                  height={56}
                  className="absolute top-0 md:-left-24 md:w-16 w-12 md:h-16 h-12 text-white bg-gold-400 p-2"
                />

                <div>
                  <h4 className="max-w-lg mr-auto md:text-2xl text-lg font-black tracking-tight mb-2 md:mt-0 mt-16">
                    Input on future enhancements
                  </h4>
                  <p className="max-w-lg mr-auto">
                    Be one of the first agents in your area to offer the
                    innovative Domoreport and deliver a superior experience for
                    your clients.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col items-center lg:w-1/2 w-full lg:p-16">
              <AffiliateContactForm
                action="https://majordomo.us19.list-manage.com/subscribe/post?u=0d8c6654c6ad1fe275a334234&amp;id=ae28f848a0"
                title="Learn More"
                referrer="For Real Estate Agents - Learn More"
                redirect={true}
              />
            </div>
          </div>
        </div>

        <div className="flex flex-col lg:mb-36 mb-12 sm:px-0 px-8 py-12">
          <div className="flex lg:flex-row flex-col">
            <div className="max-w-lg m-auto lg:w-1/2 w-full lg:mb-auto mb-8">
              <h4 className="text-center md:text-2xl text-lg font-black tracking-tight mb-8">
                Hear how the Domoreport has helped Marty Fulford, a real estate
                agent who's one of Majordomo's partners.
              </h4>

              <div className="flex lg:flex-row flex-col items-center justify-center">
                <a href="/demo" target="_blank" rel="noopener noreferrer">
                  <Button variant="primary" clipSide="right">
                    View a sample Domoreport{' '}
                    <ArrowRightIcon width={18} height={18} className="ml-2" />
                  </Button>
                </a>
              </div>
            </div>

            <div className="flex items-center justify-center lg:w-1/2 w-full">
              <iframe
                width="560"
                height="315"
                src="https://www.youtube.com/embed/CuoSJcnRG_k"
                frameBorder="0"
                className="rounded-md shadow-md"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>

        <div className="bg-gray-50  lg:mb-36 mb-12 py-12">
          <div className="flex lg:flex-row flex-col justify-evenly lg:px-16 px-4">
            <div className="space-y-4 lg:p-16 p-4 lg:w-1/2 w-full flex flex-col md:order-first order-last">
              <div>
                <h4 className="md:text-2xl text-lg font-black tracking-tight">
                  100% satisfaction
                </h4>
                <h4 className="md:text-2xl text-lg font-black tracking-tight">
                  60-day money-back guarantee
                </h4>
              </div>

              <p className="max-w-lg">
                If you don't love the Domoreport, we'll return 100% of your
                money. But we're confident you'll love it. Here’s why:
              </p>

              <p className="max-w-lg">
                Majordomo's repair-estimate data comes from analysts who study
                contractor and insurance invoices for 20,000 projects from
                35,000 providers for material, equipment, labor and installation
                costs across the US. This lets us create highly accurate
                localized estimates for your specific market.{' '}
                <a
                  href="/blog/majordomos-repair-cost-estimates-are-local-and-accurate/"
                  className="text-accentBlue hover:underline"
                >
                  Learn More
                </a>
              </p>

              <p className="max-w-lg">
                We invite you to use the Domoreport with your home inspection
                and receive repair estimates within 24 hours. We're confident
                you'll be impressed with its completeness, readability and
                accuracy.
              </p>

              <p className="max-w-lg">
                So here's our offer: If you don’t love the Domoreport or if the
                estimates aren't accurate, we'll refund 100% of your money. You
                have nothing to lose by giving it a try.
              </p>
            </div>

            <div className="flex items-center justify-center lg:w-1/2 w-full lg:p-16 md:order-last order-first">
              <img
                src="/images/product-pages/satisfaction.png"
                className="md:max-w-md max-w-xs"
              />
            </div>
          </div>

          <div className="flex md:flex-row flex-col justify-evenly items-center w-full md:space-y-0 space-y-4 md:pb-12 pb-0 md:pt-0 pt-8">
            <Link href="/checkout">
              <Button variant="primary" clipSide="right">
                Get repair estimates{' '}
                <ArrowRightIcon width={18} height={18} className="ml-2" />
              </Button>
            </Link>

            <Link href="/features/domoreport-repair-estimates">
              <a className="flex items-center justify-center font-bold text-accentBlue hover:underline">
                Learn More{' '}
                <ArrowRightIcon width={18} height={18} className="ml-2" />
              </a>
            </Link>
          </div>
        </div>

        <div className="flex flex-col lg:mb-36 mb-12 py-16">
          <div>
            <h4 className="max-w-lg m-auto md:text-2xl text-lg text-center font-black tracking-tight mb-2">
              Our Affiliations
            </h4>
            <p className="text-center md:max-w-lg max-w-sm m-auto">
              Majordomo is proud to be affiliated with InterNACHI and ASHI. Both
              international organizations aspire to improve home inspections,
              the overall home buying & ownership experience
            </p>
          </div>

          <div className="flex md:flex-row flex-col">
            <div className="flex flex-col justify-center md:w-1/2 w-full md:px-16 p-8">
              <img
                src="/images/product-pages/internachi-logo.png"
                className="w-40 self-center"
              />

              <a
                href="https://www.nachi.org/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <h4 className="md:text-2xl text-lg text-accentBlue font-black hover:underline cursor-pointer">
                  InterNACHI
                </h4>
              </a>

              <p>
                The world’s leading association for home inspectors with free
                training, online certification, business development, and more.
              </p>
            </div>

            <div className="flex flex-col justify-center md:w-1/2 w-full md:px-16 p-8">
              <img
                src="/images/product-pages/ashi-logo.png"
                className="w-36 self-center"
              />

              <a
                href="https://www.homeinspector.org/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <h4 className="md:text-2xl text-lg text-accentBlue font-black hover:underline cursor-pointer">
                  ASHI
                </h4>
              </a>

              <p>
                Committed to progressing the home inspection profession and
                providing resources to promote healthy home ownership practices.
              </p>
            </div>
          </div>
        </div>

        <h4 className="text-center md:text-2xl text-lg font-black tracking-tight md:px-0 px-8 lg:pb-36 pb-12 md:max-w-3xl max-w-md mx-auto">
          Looking for materials to share with clients? Check out our{' '}
          <Link href="/home-agent-toolkit">
            <a className="text-accentBlue hover:underline">Agent Toolkit</a>
          </Link>
        </h4>
      </main>

      <Footer />
    </>
  )
}
